import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, FileText, Users } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function ESGPage() {
  return (
    <main data-testid="esg-page" className="pt-24">
      <SEOHead path="/esg" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                ESG & dokumentation
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-6 leading-[1.1]">
                Dokumentation der kan bruges i praksis
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-[1.8]">
                Vi reducerer ikke kun energiforbrug — vi dokumenterer effekten. Alle projekter 
                kan måles før og efter installation, så virksomheden får konkrete data på 
                energibesparelse, effektreduktion og energiforbrug til brug i ESG-rapportering, 
                energiledelse og intern opfølgning.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" 
                  src="/images/film-grabs/still-8.jpg" 
                  alt="ESG Dokumentation"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ESG Cards */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Leaf,
                letter: 'E',
                title: 'Environment',
                items: [
                  'Dokumenteret reduktion af energiforbrug',
                  'Målbare energibesparelser baseret på før/efter-data',
                  'Potentiel reduktion af CO₂-belastning baseret på energiforbrug',
                  'Bidrag til virksomhedens grønne omstilling',
                ],
                description: 'LED-belysning og intelligent styring kan typisk reducere energiforbruget med 60–85 %, afhængigt af installation, driftstid og eksisterende anlæg.',
              },
              {
                icon: Users,
                letter: 'S',
                title: 'Social',
                items: [
                  'Mere stabil og ensartet belysning',
                  'Bedre lysforhold i arbejdsområder',
                  'Mindre flimmer og bedre visuel komfort',
                  'Mere funktionelle arbejds- og fællesområder',
                ],
                description: 'Moderne LED-belysning giver et mere stabilt lysmiljø end ældre installationer og kan forbedre komfort og funktionalitet i dagligdagen.',
              },
              {
                icon: FileText,
                letter: 'G',
                title: 'Governance',
                items: [
                  'Dokumentation før og efter installation',
                  'Transparente energidata og beregninger',
                  'Overblik over installation og effekt',
                  'Dokumentation til intern rapportering og ESG-arbejde',
                ],
                description: 'Alle projekter kan dokumenteres med målinger af energiforbrug, effekt og besparelse, så virksomheden får et tydeligt beslutnings- og rapporteringsgrundlag.',
              },
            ].map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 md:p-10 border border-[#A6B8A2]"
              >
                <div className="flex items-center gap-4 mb-6">
                  <area.icon size={32} strokeWidth={1} className="text-[#0E3D24]" />
                  <span className="font-['Cormorant_Garamond'] text-4xl font-light text-[#A6B8A2]">
                    {area.letter}
                  </span>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-6">
                  {area.title}
                </h3>
                <ul className="space-y-3">
                  {area.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#1E2A24]/70 text-sm">
                      <span className="text-[#A6B8A2]">—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {area.description && (
                  <p className="text-[#1E2A24]/50 text-sm mt-4 pt-4 border-t border-[#A6B8A2]/20">
                    {area.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Typiske resultater */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white">
              Typiske resultater
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { value: '60–85%', label: 'Mulig reduktion i elforbrug afhængigt af installation og styring' },
              { value: 'Dokumenterbar effekt', label: 'Energiforbrug måles før og efter installation' },
              { value: '2–3 år', label: 'Typisk tilbagebetalingstid afhængigt af driftstid og energipriser' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white">
                  {stat.value}
                </div>
                <p className="text-white/50 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dokumentationsproces */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Proces
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6 mb-12">
                Dokumentationsproces
              </h2>

              <div className="space-y-8">
                {[
                  { step: '01', title: 'Baseline', desc: 'Måling af eksisterende installation og energiforbrug' },
                  { step: '02', title: 'Implementering', desc: 'Udskiftning eller installation af LED-belysning' },
                  { step: '03', title: 'Effektmåling', desc: 'Sammenligning af energiforbrug før og efter' },
                  { step: '04', title: 'Rapportering', desc: 'Dokumentation der kan anvendes i drift og ESG-rapportering' },
                ].map((item, index) => (
                  <div key={index} className="flex gap-6">
                    <span className="text-[#A6B8A2] text-sm w-8">{item.step}</span>
                    <div>
                      <h4 className="font-medium text-[#1E2A24]">{item.title}</h4>
                      <p className="text-[#1E2A24]/70 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white p-8 md:p-10 border border-[#A6B8A2]">
                <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#0E3D24] mb-2">
                  Eksempel på dokumentation
                </h3>
                <p className="text-[#1E2A24]/50 text-sm mb-6">
                  Eksempel fra beregning på udskiftning af lysstofrør til LED:
                </p>
                <div className="space-y-4">
                  {[
                    { label: 'Før (kWh/år)', value: '3.037' },
                    { label: 'Efter (kWh/år)', value: '978' },
                    { label: 'Besparelse (kWh/år)', value: '2.059', highlight: true },
                    { label: 'Effektreduktion', value: '67,8%', highlight: true },
                  ].map((row, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-[#A6B8A2]/30 last:border-0">
                      <span className="text-[#1E2A24]/70">{row.label}</span>
                      <span className={row.highlight ? 'font-medium text-[#0E3D24]' : 'text-[#1E2A24]'}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#0E3D24] mb-8">
              Brug for dokumenteret energibesparelse?
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-[#0E3D24] text-white px-8 py-4 hover:bg-[#1F5A3A] transition-colors group"
            >
              <span>Kontakt os</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
